import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { PortalUser } from "@/types";

function usePortalUsersClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

function useInvalidatePortalUsers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.resource("portal-users") });
}

export function useCreatePortalUser(): UseMutationResult<
  PortalUser,
  Error,
  { email: string; password: string }
> {
  const getClient = usePortalUsersClient();
  const invalidate = useInvalidatePortalUsers();
  return useMutation({
    mutationFn: (input) => getClient().post<PortalUser>("/auth/users", input),
    onSuccess: invalidate,
  });
}

export function useDeletePortalUser(): UseMutationResult<{ ok: boolean }, Error, string> {
  const getClient = usePortalUsersClient();
  const invalidate = useInvalidatePortalUsers();
  return useMutation({
    mutationFn: (id) => getClient().delete<{ ok: boolean }>(`/auth/users/${id}`),
    onSuccess: invalidate,
  });
}

export function useChangePortalUserPassword(): UseMutationResult<
  { ok: boolean },
  Error,
  { id: string; password: string }
> {
  const getClient = usePortalUsersClient();
  return useMutation({
    mutationFn: ({ id, password }) =>
      getClient().put<{ ok: boolean }>(`/auth/users/${id}/password`, { password }),
  });
}
